// RecentEventsPage
// Read-only quick-look surface backed by the 14-day Azure Table Storage event
// mirror that the data-pipeline team delivered on 2026-05-12. Reads the seven
// SilverM365* event tables on log0data0us0east1 via /api/recent-events.
//
// Strict design constraints (per the user):
//   - Does NOT touch Detection Alerts, investigations, or runbooks.
//   - Always-visible left sidebar link.
//   - Sortable + per-column filterable + paginated table.
//   - Manual Refresh button (no auto-poll in v1).
//   - Visible load-time indicator so the user can judge whether the current scope
//     (1h cross-tenant by default) is too broad to be useful.
//   - Row expansion with structured Source Event view + Raw JSON copy, identical
//     in spirit to the Detection Alerts page. Underlying components live in
//     `frontend/src/components/sourceEvents/` and are intended to be the shared
//     source of truth for Alerts, Test Run Results, and Recent Events (see
//     CLAUDE.md three-way display parity rule, currently Phase 1 — only Recent
//     Events consumes the shared module; Alerts/TestRunResults migration is Phase 2).

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  ArrowPathIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import {
  fetchTenants,
  fetchRecentEvents,
  fetchRecentEventSourceEvent,
} from '../api/client';
import Pagination from './Pagination';
import { RefreshButton } from './ui/LoadingState';
import {
  SourceEventRenderer,
  RawEventJsonPanel,
  FormatTs,
  IpLink,
} from './sourceEvents';
import {
  TIMEZONE_OPTIONS,
  TZ_STORAGE_KEY,
  getDefaultTimezone,
  getTzLabel,
  getTzOffset,
  resolveIana,
} from './TimelineView';
import {
  compareVals,
  applyColFilters,
  countActiveFilters,
  useResizableCols,
  SortIndicator,
  FilterCell,
} from './coverage/coverageShared';

// ── Static configuration ─────────────────────────────────────────────────────

const LOG_TYPE_OPTIONS = [
  { key: 'graph_signin',      label: 'Sign-In (Graph)' },
  { key: 'directory_audit',   label: 'Directory Audit' },
  { key: 'url_click',         label: 'URL Click' },
  { key: 'risk_detection',    label: 'Risk Detection' },
  { key: 'risky_user',        label: 'Risky User' },
  { key: 'security_incident', label: 'Security Incident' },
  { key: 'device_security',   label: 'Device Security' },
];
const ALL_LOG_TYPE_KEYS = LOG_TYPE_OPTIONS.map((l) => l.key);
const LOG_TYPE_LABEL_BY_KEY = Object.fromEntries(LOG_TYPE_OPTIONS.map((l) => [l.key, l.label]));

// Default workloads pre-checked on mount. Device Security is opt-in — its
// rows are usually high-volume / low-signal for the read-only quick-look use
// case, and including it by default crowds out the workloads analysts come
// here to scan. Users can still toggle it on via the workload dropdown.
const DEFAULT_LOG_TYPE_KEYS = ALL_LOG_TYPE_KEYS.filter((k) => k !== 'device_security');

// 4-hour minimum lookback — anything tighter returns very few rows for most
// workloads (per-tenant volumes are low) and isn't worth the round-trip.
const HOURS_OPTIONS = [
  { value: 4,  label: '4 hours'  },
  { value: 12, label: '12 hours' },
  { value: 24, label: '24 hours' },
  { value: 72, label: '72 hours' },
];

// Data table columns. `sort` controls header click → sort field; `filterType`
// drives the per-column filter cell in the second header row.
// Tenant column intentionally omitted — tenant pick is required at the toolbar
// (v1.54.2+), so every row in the table shares the chosen tenant.
const COLS = [
  { key: 'eventTs',        label: 'Event Time', w: 210, align: 'left', filterType: 'date-op',      sort: true },
  { key: 'logType',        label: 'Log Type',   w: 170, align: 'left', filterType: 'multi-select', sort: true },
  { key: 'userId',         label: 'User',       w: 220, align: 'left', filterType: 'text',         sort: true },
  { key: 'operation',      label: 'Operation',  w: 180, align: 'left', filterType: 'text',         sort: true },
  { key: 'clientIp',       label: 'IP',         w: 140, align: 'left', filterType: 'text',         sort: true },
  { key: 'appDisplayName', label: 'App',        w: 180, align: 'left', filterType: 'text',         sort: true },
  { key: 'resultStatus',   label: 'Result',     w: 100, align: 'left', filterType: 'text',         sort: true },
];

const initFilters = () => ({
  eventTs: '', eventTs_op: '>=',
  logType: '',
  userId: '', operation: '', clientIp: '', appDisplayName: '', resultStatus: '',
});

// ── Workload filter dropdown ─────────────────────────────────────────────────
//
// Visual pattern mirrors MultiSelectFilter (coverageShared.jsx:128) but as a
// standalone toolbar control with explicit labeling, "Select all / Clear all"
// helpers, and a count summary on the button. Replaces the v1.54.x chip row
// which didn't read as a filter affordance.
function WorkloadFilterDropdown({ logTypeSet, setLogTypeSet }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  const toggle = (key) => {
    setLogTypeSet((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const selectAll = () => setLogTypeSet(new Set(ALL_LOG_TYPE_KEYS));
  const clearAll  = () => setLogTypeSet(new Set());

  const count = logTypeSet.size;
  const buttonLabel = count === ALL_LOG_TYPE_KEYS.length
    ? `Workloads (all ${ALL_LOG_TYPE_KEYS.length})`
    : count === 0
      ? 'Workloads (none — pick at least one)'
      : `Workloads (${count} of ${ALL_LOG_TYPE_KEYS.length})`;
  const buttonClass = count === 0
    ? 'input-field py-1 px-2 text-xs min-w-[220px] text-left flex items-center justify-between text-amber-400'
    : 'input-field py-1 px-2 text-xs min-w-[220px] text-left flex items-center justify-between';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={buttonClass}
        title="Pick which workloads to include in the query"
      >
        <span className="truncate">{buttonLabel}</span>
        <ChevronDownIcon className="h-3 w-3 text-gray-500 flex-shrink-0 ml-1" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 left-0 min-w-[260px] max-h-80 overflow-y-auto bg-gray-900 border border-gray-700 rounded shadow-lg py-1">
          {LOG_TYPE_OPTIONS.map((lt) => {
            const checked = logTypeSet.has(lt.key);
            return (
              <label
                key={lt.key}
                className="flex items-center gap-2 px-3 py-1.5 text-xs text-gray-300 hover:bg-gray-800 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggle(lt.key)}
                  className="h-3.5 w-3.5 accent-blue-500"
                />
                <span>{lt.label}</span>
              </label>
            );
          })}
          <div className="border-t border-gray-800 mt-1 pt-1 flex items-center gap-3 px-3 py-1.5">
            <button
              type="button"
              onClick={selectAll}
              className="text-[11px] text-blue-400 hover:text-blue-300"
            >Select all</button>
            <span className="text-gray-700">·</span>
            <button
              type="button"
              onClick={clearAll}
              className="text-[11px] text-blue-400 hover:text-blue-300"
            >Clear all</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Component ────────────────────────────────────────────────────────────────

export default function RecentEventsPage() {
  // ── Tenants (for tenant filter dropdown + short-name resolution on rows) ──
  const [tenants, setTenants] = useState([]);
  const [tenantsLoading, setTenantsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    fetchTenants()
      .then((data) => {
        if (cancelled) return;
        const active = (Array.isArray(data) ? data : []).filter((t) => t.Status !== 'inactive');
        setTenants(active);
      })
      .catch(() => { /* non-fatal — page works without a tenant map */ })
      .finally(() => { if (!cancelled) setTenantsLoading(false); });
    return () => { cancelled = true; };
  }, []);

  // ── Toolbar state ────────────────────────────────────────────────────────
  // Tenant pick is required (per v1.54.2 — cross-tenant scans against the seven
  // SilverM365* tables are unsafe due to PartitionKey lexical-bleed, blew the
  // Function App memory on v1.54.0 testing).
  const [tenantId, setTenantId] = useState('');
  const [logTypeSet, setLogTypeSet] = useState(() => new Set(DEFAULT_LOG_TYPE_KEYS));
  const [hoursLookback, setHoursLookback] = useState(4);
  const [search, setSearch] = useState('');

  // ── Data state ───────────────────────────────────────────────────────────
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastRefreshUtc, setLastRefreshUtc] = useState(null);
  const [loadTimeMs, setLoadTimeMs] = useState(null);
  // Per-workload truncation — populated from the API envelope. Empty array =
  // no workload hit its cap; an array of log_type keys = those specific workloads
  // had > perWorkloadCap rows in the window and were trimmed to the most-recent N.
  const [truncatedWorkloads, setTruncatedWorkloads] = useState([]);
  const [perWorkloadCap, setPerWorkloadCap] = useState(500);

  // ── View state ───────────────────────────────────────────────────────────
  const [selectedTz, setSelectedTz] = useState(getDefaultTimezone);
  useEffect(() => {
    try { localStorage.setItem(TZ_STORAGE_KEY, selectedTz); } catch { /* ignore */ }
  }, [selectedTz]);
  const tzObj = TIMEZONE_OPTIONS.find((t) => t.id === selectedTz) || TIMEZONE_OPTIONS[0];
  // resolveIana maps the "Local" empty iana → browser zone. Passing '' straight
  // into toLocale*/Intl throws RangeError (e.g. the unguarded load-status
  // toLocaleTimeString below), which blanks the page. See feedback-local-tz-empty-iana.
  const tzIana = resolveIana(tzObj.iana);

  const [expanded, setExpanded] = useState(() => new Set());
  const toggleExpand = useCallback((eventId) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  }, []);

  const [filters, setFilters] = useState(initFilters());
  const [sortField, setSortField] = useState('eventTs');
  const [sortDir, setSortDir] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const setFilter = useCallback((key, value) => {
    setFilters((p) => ({ ...p, [key]: value }));
    setCurrentPage(1);
  }, []);

  const clearFilters = useCallback(() => {
    setFilters(initFilters());
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback((field) => {
    setSortField((prev) => {
      if (prev === field) {
        setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        return field;
      }
      setSortDir('desc');
      return field;
    });
    setCurrentPage(1);
  }, []);

  // ── Refresh / fetch ──────────────────────────────────────────────────────
  const doRefresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    setExpanded(new Set());
    const start = performance.now();
    try {
      const logTypes = Array.from(logTypeSet);
      const result = await fetchRecentEvents({
        tenantId,   // required — UI disables Refresh until tenantId is set
        logTypes: logTypes.length === ALL_LOG_TYPE_KEYS.length ? undefined : logTypes,
        hours: hoursLookback,
        search: search || undefined,
      });
      setEvents(result.events);
      setTruncatedWorkloads(result.truncatedWorkloads || []);
      if (typeof result.perWorkloadCap === 'number' && result.perWorkloadCap > 0) {
        setPerWorkloadCap(result.perWorkloadCap);
      }
    } catch (err) {
      setError(err?.message || 'Failed to load recent events.');
      setEvents([]);
      setTruncatedWorkloads([]);
    } finally {
      const elapsed = performance.now() - start;
      setLoadTimeMs(elapsed);
      setLastRefreshUtc(new Date());
      setLoading(false);
    }
  }, [tenantId, logTypeSet, hoursLookback, search]);

  // NO auto-fetch on mount. The default 1h cross-tenant scan can be slow on
  // busy tenant populations (initial UX test showed it as too sluggish to be
  // the page's first impression). The user picks their scope from the toolbar
  // and explicitly clicks Refresh — the page is instant on first load and the
  // load-time indicator tells them whether their chosen scope is tractable.

  // ── Derived rows ─────────────────────────────────────────────────────────
  // Tenant scope is fixed by the toolbar, so no per-row tenant resolution needed.
  const rows = events || [];

  // Distinct values for select / multi-select filter dropdowns.
  const distinct = useMemo(() => ({
    logType: [...new Set(rows.map((r) => r.logType).filter(Boolean))].sort(),
  }), [rows]);

  // ── Filter + sort pipeline ───────────────────────────────────────────────
  const filtered = useMemo(() => {
    let r = applyColFilters(rows, COLS, filters);
    if (sortField) {
      r = [...r].sort((a, b) => compareVals(a[sortField], b[sortField], sortDir));
    }
    return r;
  }, [rows, filters, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = useMemo(
    () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filtered, currentPage, pageSize]
  );

  const activeFilterCount = countActiveFilters(filters);

  // ── Column-resize state (position-indexed; we never hide cols on this page) ──
  const { colWidths, thRefs, tableRef, startResize } = useResizableCols(COLS.map((c) => c.w));
  const chevronColWidth = 32;
  const totalWidth = chevronColWidth + colWidths.reduce((a, b) => a + b, 0);

  // ── Renderable status strip (load timer + last refresh) ──────────────────
  const refreshStatus = (() => {
    if (loading) return <span className="text-blue-400">Loading…</span>;
    if (error)   return <span className="text-red-400">{error}</span>;
    if (loadTimeMs == null) return null;
    const rowsLabel = filtered.length === 1 ? '1 row' : `${filtered.length.toLocaleString()} rows`;
    const totalLabel = filtered.length === events.length
      ? rowsLabel
      : `${rowsLabel} (of ${events.length.toLocaleString()})`;
    const seconds = (loadTimeMs / 1000).toFixed(1);
    const refreshedAt = lastRefreshUtc
      ? lastRefreshUtc.toLocaleTimeString('en-US', {
          timeZone: tzIana,
          hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
        })
      : '';
    return (
      <span>
        Loaded {totalLabel} in <span className="text-gray-300">{seconds}s</span>
        {refreshedAt && (
          <> · last refreshed <span className="text-gray-400">{refreshedAt} {getTzLabel(selectedTz)}</span></>
        )}
      </span>
    );
  })();

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="p-6 space-y-6">
      {/* Page header */}
      <div>
        <div className="text-xs text-gray-500 mb-1">Quick Look</div>
        <h1 className="text-xl font-semibold text-gray-100 flex items-center gap-2">
          <ClockIcon className="h-5 w-5 text-blue-400" />
          Recent Events
        </h1>
        <p className="text-xs text-gray-500 mt-1">
          Read-only view of the rolling 14-day event mirror across seven workloads. Sorting, filtering,
          and pagination are all client-side. The lookback window caps the underlying query — pick a tighter
          window if the load time grows.
        </p>
      </div>

      {/* Toolbar — tenant + log types + lookback + search + refresh */}
      <div className="card p-4 space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          {/* Tenant filter (required) */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 whitespace-nowrap">Tenant</label>
            <select
              value={tenantId}
              onChange={(e) => setTenantId(e.target.value)}
              disabled={tenantsLoading}
              className="input-field py-1 px-2 text-xs min-w-[200px]"
              required
            >
              <option value="">{tenantsLoading ? 'Loading tenants…' : '— Select a tenant —'}</option>
              {tenants
                .map((t) => {
                  const shortName = t.ShortName;
                  const friendlyName = t.FriendlyName;
                  const label = shortName && friendlyName
                    ? `(${shortName}) ${friendlyName}`
                    : (shortName || friendlyName || t.Domain || t.TenantID);
                  return { id: t.TenantID, label };
                })
                .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))
                .map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </div>

          {/* Workload filter dropdown */}
          <WorkloadFilterDropdown logTypeSet={logTypeSet} setLogTypeSet={setLogTypeSet} />

          {/* Lookback dropdown */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 whitespace-nowrap">Lookback</label>
            <select
              value={hoursLookback}
              onChange={(e) => setHoursLookback(Number(e.target.value))}
              className="input-field py-1 px-2 text-xs"
            >
              {HOURS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Search box (server-side OR across user / op / IP / app) */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 whitespace-nowrap">Search</label>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') doRefresh(); }}
              placeholder="user / op / IP / app"
              className="input-field py-1 px-2 text-xs min-w-[200px]"
            />
          </div>

          {/* Refresh button — disabled until a tenant is picked */}
          <RefreshButton
            loading={loading}
            onClick={doRefresh}
            disabled={!tenantId || logTypeSet.size === 0}
            title={!tenantId ? 'Pick a tenant first' : (logTypeSet.size === 0 ? 'Pick at least one workload' : 'Refresh')}
            className="btn-primary text-xs px-3 py-1.5 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          />

          {/* Load timer + last refresh */}
          <div className="text-[11px] text-gray-500 flex-1 min-w-[8rem]">
            {refreshStatus}
            {truncatedWorkloads.length > 0 && (
              <span
                className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded border border-amber-700/60 bg-amber-900/30 text-amber-300 text-[10px]"
                title={`These workloads had more than ${perWorkloadCap} rows in the chosen window; only the most-recent ${perWorkloadCap} were returned. Narrow the lookback to see older rows.`}
              >
                Truncated: {truncatedWorkloads.map((k) => LOG_TYPE_LABEL_BY_KEY[k] || k).join(', ')} · most-recent {perWorkloadCap} returned
              </span>
            )}
          </div>

          {/* Timezone picker — shared with Alerts + Coverage pages */}
          <div className="flex items-center gap-2">
            <label className="text-xs text-gray-400 whitespace-nowrap">Time zone</label>
            <select
              value={selectedTz}
              onChange={(e) => setSelectedTz(e.target.value)}
              className="input-field py-1 px-2 text-xs"
            >
              {TIMEZONE_OPTIONS.map((tz) => (
                <option key={tz.id} value={tz.id}>{tz.label} ({getTzOffset(tz.iana)})</option>
              ))}
            </select>
          </div>
        </div>

      </div>

      {/* Results table */}
      <div className="card overflow-hidden">
        {/* Toolbar above table — active-filter clear button */}
        <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-gray-800 min-h-[40px]">
          <div className="flex-1 min-w-[8rem] text-xs text-gray-500">
            {filtered.length.toLocaleString()} {filtered.length === 1 ? 'event' : 'events'} after filters
          </div>
          {activeFilterCount > 0 && (
            <button
              type="button"
              onClick={clearFilters}
              className="flex items-center gap-1 text-xs text-gray-400 hover:text-gray-200 border border-gray-700 hover:border-gray-600 rounded px-2 py-0.5"
            >
              Clear {activeFilterCount} filter{activeFilterCount !== 1 ? 's' : ''}
            </button>
          )}
        </div>

        <div className="overflow-x-auto">
          <table ref={tableRef} className="text-xs text-gray-300" style={{ tableLayout: 'fixed', width: `${totalWidth}px` }}>
            <thead>
              {/* Header row — chevron col + sortable data cols */}
              <tr className="border-b border-gray-800 bg-gray-900/60">
                <th style={{ width: `${chevronColWidth}px` }} className="px-2 py-2"></th>
                {COLS.map((col, i) => (
                  <th
                    key={col.key}
                    ref={(el) => { thRefs.current[i] = el; }}
                    style={{ width: `${colWidths[i]}px` }}
                    className={`px-3 py-2 font-medium text-gray-400 whitespace-nowrap relative select-none cursor-pointer ${col.align === 'center' ? 'text-center' : 'text-left'}`}
                    onClick={() => col.sort && handleSort(col.key)}
                  >
                    <span className="text-xs">
                      {col.key === 'eventTs' ? `Event Time (${getTzLabel(selectedTz)})` : col.label}
                    </span>
                    <SortIndicator active={sortField === col.key} dir={sortDir} />
                    <div
                      className="absolute top-0 right-0 h-full w-2 cursor-col-resize z-10 flex items-stretch justify-center group"
                      onMouseDown={(e) => { e.stopPropagation(); startResize(e, i); }}
                    >
                      <div className="w-px bg-gray-700/50 group-hover:bg-blue-500/70 transition-colors" />
                    </div>
                  </th>
                ))}
              </tr>
              {/* Filter row — per-column inputs from coverageShared FilterCell */}
              <tr className="border-b border-gray-800 bg-gray-900/30">
                <td style={{ width: `${chevronColWidth}px` }} className="px-1.5 py-1"></td>
                {COLS.map((col, i) => (
                  <td key={col.key} style={{ width: `${colWidths[i]}px` }} className="px-1.5 py-1">
                    <FilterCell col={col} filters={filters} onFilterChange={setFilter} distinct={distinct} />
                  </td>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.length === 0 ? (
                <tr>
                  <td colSpan={COLS.length + 1} className="px-3 py-8 text-center text-gray-500">
                    {loading
                      ? 'Loading recent events…'
                      : lastRefreshUtc === null
                        ? (tenantId
                          ? 'Click Refresh to load events for the selected scope.'
                          : 'Pick a tenant above, then click Refresh.')
                        : events.length === 0
                          ? 'No events returned. Try widening the lookback or selecting more workloads.'
                          : 'No events match the current filters.'}
                  </td>
                </tr>
              ) : (
                paged.map((e) => (
                  <RecentEventRow
                    key={`${e.logType}:${e.eventId}`}
                    event={e}
                    expanded={expanded.has(e.eventId)}
                    onToggle={() => toggleExpand(e.eventId)}
                    tzIana={tzIana}
                    chevronColWidth={chevronColWidth}
                  />
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {filtered.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            pageSize={pageSize}
            totalItems={filtered.length}
            onPageChange={setCurrentPage}
            onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }}
          />
        )}
      </div>
    </div>
  );
}

// ── Row + expanded body ──────────────────────────────────────────────────────

function RecentEventRow({ event, expanded, onToggle, tzIana, chevronColWidth }) {
  // Lazy-parse raw_event_json only when the row is expanded; keeps the table fast
  // even when 500 rows are loaded.
  const rawEvent = useMemo(() => {
    if (!expanded) return null;
    if (!event.rawEventJson) return null;
    try { return JSON.parse(event.rawEventJson); }
    catch { return null; }
  }, [expanded, event.rawEventJson]);

  const blobPath = event.rawEventBlobPath || '';
  const logTypeLabel = LOG_TYPE_LABEL_BY_KEY[event.logType] || event.logType;

  return (
    <>
      <tr
        onClick={onToggle}
        className="border-b border-gray-800/50 hover:bg-gray-800/30 cursor-pointer"
      >
        <td style={{ width: `${chevronColWidth}px` }} className="pl-3 pr-1 py-2.5">
          {expanded
            ? <ChevronDownIcon className="h-3.5 w-3.5 text-gray-500" />
            : <ChevronRightIcon className="h-3.5 w-3.5 text-gray-500" />}
        </td>
        <td className="px-3 py-2 text-gray-300 whitespace-nowrap">
          <FormatTs ts={event.eventTs} tzIana={tzIana} />
        </td>
        <td className="px-3 py-2">
          <span className="inline-flex items-center px-1.5 py-0.5 text-[10px] rounded border border-gray-700/60 bg-gray-900/40 text-gray-300">
            {logTypeLabel}
          </span>
        </td>
        <td className="px-3 py-2 font-mono text-gray-300 truncate" title={event.userId}>{event.userId || '—'}</td>
        <td className="px-3 py-2 text-gray-300 truncate" title={event.operation}>{event.operation || '—'}</td>
        <td className="px-3 py-2"><IpLink ip={event.clientIp} /></td>
        <td className="px-3 py-2 text-gray-300 truncate" title={event.appDisplayName || event.applicationId}>
          {event.appDisplayName || (event.applicationId
            ? <span className="font-mono text-[11px]">{event.applicationId}</span>
            : '—')}
        </td>
        <td className="px-3 py-2 text-gray-300">{event.resultStatus || '—'}</td>
      </tr>
      {expanded && (
        <tr className="bg-gray-900/50 border-b-2 border-gray-700">
          <td colSpan={COLS.length + 1} className="pl-10 pr-6 py-4 border-l-4 border-blue-600/40">
            <div className="space-y-3">
              <div>
                <div className="text-[10px] uppercase tracking-wide text-gray-500 mb-1">Source Event (Microsoft data)</div>
                <SourceEventRenderer
                  rawEvent={rawEvent}
                  logType={event.logType}
                  tzIana={tzIana}
                />
              </div>
              <RawEventJsonPanel
                rawEventJson={event.rawEventJson}
                blobPath={blobPath || undefined}
                fetchBlob={(bp) => fetchRecentEventSourceEvent(bp, {
                  tenantId: event.tenantId,
                  logType:  event.logType,
                  partitionKey: event.partitionKey,
                  rowKey:   event.rowKey,
                })}
              />
            </div>
          </td>
        </tr>
      )}
    </>
  );
}
